import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../Customize_Css/Pagination.css';
import '../Customize_Css/ViewVendor.css';
import '../Customize_Css/Banners.css';
import { Dropdown, Modal, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaUpload, FaTrashAlt, FaFileExport, FaEdit, FaSearch, FaEye, FaImage } from 'react-icons/fa';
import Sidebar from '../Components/Siderbar';
import Footer from '../Components/Footer';
import { toast } from 'react-toastify';
import axios from 'axios';
import * as XLSX from 'xlsx';

const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-GB', options);
};

const ViewBanners = () => {
    const epicxTenantId = localStorage.getItem('epicxTenantId');
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [selectedItems, setSelectedItems] = useState([]);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedBanner, setSelectedBanner] = useState(null);
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const AuthorizationToken = localStorage.getItem('token');

    useEffect(() => {
        fetchBanners();
    }, [epicxTenantId]);

    const fetchBanners = async () => {
        if (!epicxTenantId) {
            console.error('Tenant ID is not provided');
            return;
        }
        try {
            const response = await axios.get(`${BASE_URL}/products/api/banners/${epicxTenantId}`,
                {
                    headers: {
                        Authorization: `Bearer ${AuthorizationToken}`,
                      },
                    }
            );
            setData(response.data); 
        } catch (error) {
            console.error('Error fetching banners:', error);
            toast.error(`Error fetching banners: ${error.message}`);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
        setData((prevData) => {
            return [...prevData].sort((a, b) => {
                let aValue = a.banner[key]; // Access banner object
                let bValue = b.banner[key];

                if (key === 'bannerImage1' || key === 'bannerImage2') {
                    aValue = aValue ? aValue.length : 0;
                    bValue = bValue ? bValue.length : 0;
                }

                if (typeof aValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (aValue < bValue) return direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return direction === 'asc' ? 1 : -1;
                return 0;
            });
        });
    };

    const handleSearch = (value) => {
        setSearchTerm(value.toLowerCase());
        if (value === '') {
            fetchBanners();
            return;
        }
        const filteredData = data.filter((item) =>
            Object.keys(item.banner).some((key) =>
                item.banner[key] && item.banner[key].toString().toLowerCase().includes(value.toLowerCase())
            )
        );
        setData(filteredData);
        setCurrentPage(1);
    };

    const handleSelectAll = () => {
        if (selectedItems.length === data.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(data.map(item => item.banner._id)); // Use banner._id
        }
    };

    const handleSelectItem = (_id) => {
        if (selectedItems.includes(_id)) {
            setSelectedItems(selectedItems.filter(item => item !== _id));
        } else {
            setSelectedItems([...selectedItems, _id]);
        }
    };

    const handleDelete = async () => {
        if (selectedItems.length === 0) {
            toast.warn("No items selected to delete");
            return;
        }
        const confirmed = window.confirm("Are you sure you want to delete the selected banners?");
        if (!confirmed) return;
        try {
            for (const itemId of selectedItems) {
                await axios.put(`${BASE_URL}/products/api/banners/${epicxTenantId}/${itemId}/delete`,
                    {}, 
                    {
                      headers: {
                        Authorization: `Bearer ${AuthorizationToken}`,
                      },
                    }
                );
                console.log(`Banner with id ${itemId} deleted`);
            }
            setData(data.filter(item => !selectedItems.includes(item.banner._id)));
            setSelectedItems([]);
            toast.success("Selected banners deleted successfully!");
        } catch (error) {
            console.error("Error deleting banners:", error);
            toast.error("Error deleting banners. Please try again.");
        }
    };

    const handleExport = () => {
        const dataToExport = selectedItems.length > 0
            ? data.filter(item => selectedItems.includes(item.banner._id))
            : data;
        const exportData = dataToExport.map(item => ({
            bannerName: item.banner.bannerName,
            productVendorName: item.banner.productVendorName,
            associatedWith: item.banner.associatedWith,
            productName: item.banner.productName || item.associatedData?.productName,
            categoryName: item.banner.categoryName || item.associatedData?.categoryName,
            subCategoryName: item.banner.subCategoryName || item.associatedData?.subCategoryName,
            createdAt: item.banner.createdAt,
        }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Banners");
        XLSX.writeFile(wb, 'Banners_data.xlsx');
    };

    const handleEdit = () => {
        if (selectedItems.length === 1) {
            const [selectedId] = selectedItems;
            navigate(`/update-banner/${selectedId}`);
        } else {
            toast.error("Please select exactly one banner to edit");
        }
    };

    const handleViewMore = (item) => {
        setSelectedBanner(item); // Pass the entire item (banner + associatedData)
        setShowDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedBanner(null);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentData = data.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(data.length / itemsPerPage);

    const bannerColumns = [
        { key: 'bannerName', label: 'Banner Name' },
        { key: 'associatedWith', label: 'Associated With' },
        { key: 'bannerImage1', label: 'Banner Image 1' },
        { key: 'bannerImage2', label: 'Banner Image 2' },
        { key: 'productOrCategory', label: 'Product / Category' },
        { key: 'createdAt', label: 'Created Date' },
    ];

    const linkedName = (item) =>
        item.banner.productName ||
        item.banner.categoryName ||
        item.banner.subCategoryName ||
        item.associatedData?.productName ||
        item.associatedData?.categoryName ||
        item.associatedData?.subCategoryName ||
        'N/A';

    return (
        <>
            <Sidebar />
            <div className="vendors-page">
                <header className="vendors-hero">
                    <div>
                        <p className="vendors-kicker">Sales</p>
                        <h1>Banner List</h1>
                        <p>Browse, find, and manage store banners.</p>
                    </div>
                    <button
                        className="vendors-primary-btn"
                        onClick={() => navigate('/add-banners')}
                    >
                        <FaPlus /> Add Banner
                    </button>
                </header>

                <section className="vendors-toolbar" aria-label="Banner search and actions">
                    <div className="vendors-search">
                        <FaSearch aria-hidden="true" />
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search banners..."
                            aria-label="Search banners"
                            value={searchTerm}
                            onChange={(e) => handleSearch(e.target.value)}
                        />
                    </div>
                    <Dropdown>
                        <Dropdown.Toggle className="vendors-action-btn">
                            Action
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item>
                                <FaUpload className="me-2" /> Push to Supplier
                            </Dropdown.Item>
                            <Dropdown.Item onClick={handleDelete} disabled={selectedItems.length === 0}>
                                <FaTrashAlt className="me-2" /> Delete
                            </Dropdown.Item>
                            <Dropdown.Item onClick={handleEdit} disabled={selectedItems.length !== 1}>
                                <FaEdit className="me-2" /> Edit
                            </Dropdown.Item>
                            <Dropdown.Item onClick={handleExport}>
                                <FaFileExport className="me-2" /> Export
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </section>

                <div className="vendors-card">
                    <div className="vendors-card-head">
                        <div>
                            <h2>All Banners</h2>
                            <p>Select a banner to edit, delete, or view full details.</p>
                        </div>
                        <span className="vendors-count">
                            <FaImage aria-hidden="true" />
                            {data.length} {data.length === 1 ? 'banner' : 'banners'}
                        </span>
                    </div>
                    <div className="vendors-card-body">
                        <div className="vendors-table-wrap no-scroll">
                            <table className="vendors-table vendors-table-plain">
                                <thead>
                                    <tr>
                                        <th className="vendors-check-cell">
                                            <input
                                                className="vendors-checkbox"
                                                type="checkbox"
                                                aria-label="Select all banners"
                                                checked={selectedItems.length === data.length && data.length > 0}
                                                onChange={handleSelectAll}
                                            />
                                        </th>
                                        {bannerColumns.map((column) => (
                                            <th key={column.key} onClick={() => handleSort(column.key)}>
                                                {column.label}
                                                {sortConfig.key === column.key ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                                            </th>
                                        ))}
                                        <th className="vendors-action-cell">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentData.length > 0 ? currentData.map((item) => (
                                        <tr key={item.banner._id} className={selectedItems.includes(item.banner._id) ? 'is-selected' : ''}>
                                            <td className="vendors-check-cell">
                                                <input
                                                    className="vendors-checkbox"
                                                    type="checkbox"
                                                    aria-label={`Select ${item.banner.bannerName}`}
                                                    checked={selectedItems.includes(item.banner._id)}
                                                    onChange={() => handleSelectItem(item.banner._id)}
                                                />
                                            </td>
                                            <td><span className="vendor-name">{item.banner.bannerName}</span></td>
                                            <td><span className="banner-chip">{item.banner.associatedWith}</span></td>
                                            <td className="banner-image-cell">
                                                {item.banner.bannerImage1 ? (
                                                    <img className="banner-thumb" src={item.banner.bannerImage1} alt={item.banner.bannerName} />
                                                ) : 'No Image'}
                                            </td>
                                            <td className="banner-image-cell">
                                                {item.banner.bannerImage2 ? (
                                                    <img className="banner-thumb" src={item.banner.bannerImage2} alt={item.banner.bannerName} />
                                                ) : 'No Image'}
                                            </td>
                                            <td>{linkedName(item)}</td>
                                            <td>{formatDate(item.banner.createdAt)}</td>
                                            <td className="vendors-action-cell">
                                                <button
                                                    type="button"
                                                    className="vendors-view-btn"
                                                    onClick={() => handleViewMore(item)}
                                                >
                                                    <FaEye aria-hidden="true" /> View More
                                                </button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="8">
                                                <div className="vendors-empty">
                                                    <span><FaImage aria-hidden="true" /></span>
                                                    <h3>No banners found</h3>
                                                    <p>Try another search or add a new banner.</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <div className="vendors-card-footer">
                        <span className="vendors-page-summary">
                            {data.length > 0
                                ? `Showing ${indexOfFirstItem + 1}–${Math.min(indexOfLastItem, data.length)} of ${data.length}`
                                : 'Showing 0 banners'}
                        </span>
                        <div className="vendors-pagination">
                            <nav>
                                <ul className="pagination mb-0" aria-label="Banners pagination">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link btn-sm" onClick={() => setCurrentPage(1)} aria-label="First page">&laquo;</button>
                                    </li>
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link btn-sm" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} aria-label="Previous page">&lt;</button>
                                    </li>
                                    <li className="page-item active">
                                        <button className="page-link btn-sm" aria-current="page">{currentPage}</button>
                                    </li>
                                    <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                                        <button className="page-link btn-sm" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages || 1))} aria-label="Next page">&gt;</button>
                                    </li>
                                    <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                                        <button className="page-link btn-sm" onClick={() => setCurrentPage(totalPages || 1)} aria-label="Last page">&raquo;</button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>

            <Modal show={showDetailsModal} onHide={handleCloseDetailsModal} size="lg" centered dialogClassName="vendor-details-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{selectedBanner?.banner.bannerName || 'Banner Details'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedBanner ? (
                        <div className="vendor-details-content">
                            <div className="vendor-details-title">
                                {selectedBanner.banner.bannerImage1 ? (
                                    <img className="vendor-details-photo" src={selectedBanner.banner.bannerImage1} alt={selectedBanner.banner.bannerName} />
                                ) : (
                                    <span><FaImage aria-hidden="true" /></span>
                                )}
                                <div>
                                    <small>Banner</small>
                                    <h3>{selectedBanner.banner.bannerName}</h3>
                                    <p>{selectedBanner.banner.associatedWith || 'Not linked'}</p>
                                </div>
                            </div>

                            <section className="vendor-details-section">
                                <h4><FaImage aria-hidden="true" /> Banner Images</h4>
                                <div className="banner-details-images">
                                    {selectedBanner.banner.bannerImage1 && (
                                        <img src={selectedBanner.banner.bannerImage1} alt="Banner 1" />
                                    )}
                                    {selectedBanner.banner.bannerImage2 && (
                                        <img src={selectedBanner.banner.bannerImage2} alt="Banner 2" />
                                    )}
                                    {!selectedBanner.banner.bannerImage1 && !selectedBanner.banner.bannerImage2 && (
                                        <p className="vendor-details-empty">No Images Available</p>
                                    )}
                                </div>
                            </section>

                            <div className="vendor-details-grid">
                                <div><span>Banner Name</span><strong>{selectedBanner.banner.bannerName}</strong></div>
                                <div><span>Associated With</span><strong>{selectedBanner.banner.associatedWith || 'N/A'}</strong></div>
                                <div><span>Vendor Name</span><strong>{selectedBanner.banner.productVendorName || 'N/A'}</strong></div>
                                {selectedBanner.associatedData ? (
                                    selectedBanner.banner.associatedWith === 'Product' ? (
                                        <>
                                            <div><span>Product Name</span><strong>{selectedBanner.associatedData.productName || 'N/A'}</strong></div>
                                            <div><span>Product Type</span><strong>{selectedBanner.associatedData.productType || 'N/A'}</strong></div>
                                            <div><span>Category</span><strong>{selectedBanner.associatedData.categoryName || 'N/A'}</strong></div>
                                        </>
                                    ) : selectedBanner.banner.associatedWith === 'Category' ? (
                                        <div><span>Category Name</span><strong>{selectedBanner.associatedData.categoryName || 'N/A'}</strong></div>
                                    ) : (
                                        <div><span>SubCategory Name</span><strong>{selectedBanner.associatedData.subCategoryName || 'N/A'}</strong></div>
                                    )
                                ) : (
                                    <div className="details-wide"><span>Associated Data</span><strong>No Associated Data Available</strong></div>
                                )}
                                <div><span>Created At</span><strong>{formatDate(selectedBanner.banner.createdAt)}</strong></div>
                                {selectedBanner.banner.updatedAt && (
                                    <div><span>Updated At</span><strong>{formatDate(selectedBanner.banner.updatedAt)}</strong></div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <p className="vendor-details-empty">No banner details available.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button className="vendors-modal-close" onClick={handleCloseDetailsModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
            <div className="vendors-footer">
                <Footer />
            </div>
            </div>
        </>
    );
};

export default ViewBanners;